import {useHttpClient} from '@raison/network-facade';
import {invariant} from '@raison/toolbox';
import {Outcome, TransactionId} from '@raison/types';
import {RootLayout, RsnButton, RsnDialog, RsnSkeleton, RsnTextButton} from '@raison/ui-kit';
import {QueryObserverResult, useQuery, useSuspenseQuery} from '@tanstack/react-query';
import {type} from 'arktype';
import BigNumber from 'bignumber.js';
import {absurd} from 'fp-ts/lib/function';
import {useStore} from 'jotai';
import {useAtomCallback} from 'jotai/utils';
import React, {FormEvent, useEffect, useId, useState} from 'react';
import {useNavigate} from 'react-router';
import {
    CurrencyFormatter,
    useCurrencyFormat,
    usePrimaryCurrencyFormat,
    useRateToPrimaryCurrency,
} from '~/entities/currencies';
import {GenericErrorMsg} from '~/entities/dialogs';
import {FallbackPage, NotFoundPage} from '~/entities/fallbacks';
import {TransactionHolder, TransactionHolderSchema} from '~/entities/operations-crypto';
import {OperationDetail} from '~/entities/operations-fiat';
import {executeOperationFinishNavigation} from '~/entities/transactions-history/model/TransactionDetailsPath';
import {useLogout, useUserInfoQueryOptions} from '~/entities/user';
import {
    InvalidCodeDialog,
    PinErrorCode,
    SmsDeliveryStatus,
    SmsErrorStatus,
    SusActivityDialog,
    TwoFaCodeForm,
    TwoFaMethodChoiceView,
    TwoFaProvider,
    TwoFaProviderKind,
    useResendTimer,
    useSmsStatusPolling,
} from '~/features/two-factor';
import {removeUserAlert} from '~/features/user-alerts';
import {connectSubmitToImmerForm, useImmerState, userInputRecipe} from '~/library/form-models.v1';
import {useAppDispatch} from '~/shared/store';
import {FeeRequestData, useFeesOnSendOptions} from '../api/fetchFeesOnSend';
import {submitTravelRuleCommit} from '../api/submitTravelRuleCommit';
import {submitTravelRuleConfirm} from '../api/submitTravelRuleConfirm';
import {submitTrtCancellation} from '../api/submitTrtCancellation';
import {CANCELLATION_ENABLED} from '../constants';
import {useInHistoryStateNamespace} from '../lib/useInHistoryState';
import {
    forgetEntrySteps,
    NavigateToEntry,
    useNavigateFromEntry,
    useNavigationEntryPoint,
} from '../lib/useNavigateWithCount';
import {SendTxResources, useAssetAccountResource} from '../model/SendTxContainer';
import {OrderId, TrTx} from '../model/TrTx';
import {canceledTrTxIds$} from '../model/TrTxIdsRegistry';
import {useErrorHandler} from '../model/useErrorHandler';
import {CalculatedAmounts} from './SendTxIntro';
import {TrtDetailsDialog} from './SentTrTxDetailsDialog';
import {isSentTrTxAlert} from './TrTxAlertHandlers';
import {TrTxNotEnoughFundsPage} from './TrTxNotEnoughFundsPage';
import {TwoFaConfirmationFormState} from './TwoFaConfirmationForm';
import {OrderPlacedInfoPage} from './TxPlacedPage';
import {AreYouSureCancelDialog} from './views/AreYouSureCancelDialog';
import {TxFeeInfoDialog} from './views/SendTrTx.views';

type Step = (typeof Step)[keyof typeof Step];
const Step = {
    fee: 'fee',
    twoFaPick: '2fa-pick',
    twoFaCode: '2fa-code',
    notEnoughFunds: 'not-enough-funds',
} as const;

const Direction = type.never
    .or({
        step: `undefined | '${Step.fee}'`,
    })
    .or({
        step: `'${Step.twoFaPick}'`,
    })
    .or({
        step: `'${Step.twoFaCode}'`,
        provider: TwoFaProvider,
        // FIXME: (non critical) qty this is supposed to be BigNumber but it isn't serializable, gotta use codecs
        qty: 'string',
        th: TransactionHolderSchema,
        delivery: SmsDeliveryStatus.optional(),
    })
    .or({
        step: `'${Step.notEnoughFunds}'`,
    })
    .and(
        type({
            dialog: 'undefined?',
        }).or({
            dialog: "'settings-prompt' | 'method-picker'",
        })
    );

export function SendTrTxContinuation({trt}: {trt: TrTx}) {
    const entry = useNavigationEntryPoint();
    const navigateAway = useNavigate();
    const navigate = useNavigateFromEntry(entry);
    const useInHistoryState = useInHistoryStateNamespace('send_continuation', navigate);
    const [direction, setDirection] = useInHistoryState(Direction, {step: 'fee'});

    const dispatch = useAppDispatch();

    const queryOfUser = useSuspenseQuery(useUserInfoQueryOptions());

    const amountsQuery = useQuery({
        ...useFeesOnSendOptions(FeeRequestData.fromTrTx(trt)),
    });

    const {startPolling} = useSmsStatusPolling();

    const [timer$, setTimer] = useResendTimer({seconds: 60});
    // The step lives in history, so the user can land on the code entry from elsewhere (e.g. via back navigation)
    // without a fresh send. Reset the timer on mount so "Resend code" is available right away.
    useEffect(() => {
        setTimer.success(0);
    }, []);
    const [smsForm, setSmsForm] = useImmerState(TwoFaConfirmationFormState);
    const removeAlert = useAtomCallback(get => removeUserAlert(get, isSentTrTxAlert(trt.id)));

    const http = useHttpClient();
    const logout = useLogout();
    const handleError = useErrorHandler();

    const [finish, setFinish] = useState<{amount: string; txId: OrderId}>();

    const assetR = useAssetAccountResource(trt.fromAccount.id, trt.fromAccount.type);

    if (assetR.isError) {
        return <FallbackPage />;
    }

    if (assetR.isLoading) {
        return <ContinuationFeeStepLoading />;
    }

    if (!assetR.data) {
        return <NotFoundPage />;
    }

    const submitForm = async ({repeating = false, provider}: {repeating?: boolean; provider: TwoFaProviderKind}) => {
        if (!amountsQuery.isSuccess || !amountsQuery.data.value) {
            return;
        }
        const amounts = amountsQuery.data.value;

        setTimer.pending();

        setSmsForm(form => {
            form.values.codeFromSms = '';
            form.serverIssues.codeFromSms = undefined;
        });

        const response = await submitTravelRuleConfirm(http, {
            trtId: trt.id,
            accountId: trt.fromAccount.id,
            accountType: trt.fromAccount.type,
            fee: amounts.feeAmount,
            retry: repeating,
            provider,
        }).catch(crash => {
            setTimer.failure(crash);
            return handleError(crash);
        });
        switch (response.status) {
            case 200:
            case 201:
                gotTransactionHolder(response.json);
                return;
            case 400:
                if (
                    response.json.fields?.fee?.[0]?.startsWith('Not enough crypto to pay blockchain fee') ||
                    response.json.fields?.fee?.[0]?.startsWith('Not enough crypto, must be') ||
                    response.json.fields?.fee?.[0]?.startsWith('Not enough tokens, must be')
                ) {
                    setDirection({step: Step.notEnoughFunds});
                    return;
                }
                dispatch(GenericErrorMsg.open());
                return;
            case 401:
                logout();
                break;
        }

        function gotTransactionHolder(th: TransactionHolder) {
            setDirection({step: Step.twoFaCode, th, qty: trt.amount.toString(), provider}, {replace: true});
            setTimer.success(th.expires_in);
            if (provider.kind === 'sms') {
                startPolling({
                    tenantId: th.tenant_uuid,
                    onError: onPollingError,
                    ...(repeating ? {onSuccess: onRepeatedPollingSuccess} : null),
                });
            }
        }
        function onPollingError(error: SmsErrorStatus) {
            setTimer.success(0);
            setDirection(current => (current.step === Step.twoFaCode ? {...current, delivery: error} : current));
        }

        function onRepeatedPollingSuccess() {
            setDirection(current => (current.step === Step.twoFaCode ? {...current, delivery: 'DELIVERED'} : current));
        }
    };

    if (finish) {
        return (
            <NavigateToEntry entry={entry}>
                <OrderPlacedInfoPage
                    amount={finish.amount}
                    currency={trt.currency}
                    goToDetails={() => executeOperationFinishNavigation(finish.txId, navigateAway, {replace: true})}
                    direction={'out'}
                    details={'Transfer request submitted.\nProcessing may take some time.'}
                />
            </NavigateToEntry>
        );
    } else if (direction.step === Step.notEnoughFunds) {
        return <TrTxNotEnoughFundsPage trt={trt} amounts={amountsQuery.data?.value ?? null} />;
    } else if (direction.step === Step.twoFaPick) {
        return (
            <RootLayout common title='Confirmation'>
                <TwoFaMethodChoiceView
                    onMessagePick={method => submitForm({provider: {kind: 'sms', method}})}
                    onAppPick={() => submitForm({provider: {kind: 'otp'}})}
                    onActivationPick={() => setDirection(dir => ({...dir, dialog: 'settings-prompt'}))}
                    onMethodPickerOpen={() => setDirection(dir => ({...dir, dialog: 'method-picker'}))}
                    // FIXME: HACK: "Open Settings" replaces the settings-prompt dialog entry with the settings page.
                    // After returning back that entry is gone but still counted, so NavigateToEntry
                    // on the success screen would step out of the flow. See forgetEntrySteps
                    onSettingsOpen={() => forgetEntrySteps(entry, 1)}
                    onDialogClose={() => navigate(-1)}
                    isActivationPromptOpen={direction.dialog === 'settings-prompt'}
                    isMethodPickerOpen={direction.dialog === 'method-picker'}
                    isAppActivated={queryOfUser.data.otpEnabled}
                />
            </RootLayout>
        );
    } else if (direction.step === Step.twoFaCode) {
        const {th: holder, qty: amount, provider} = direction;

        const connectSubmit = connectSubmitToImmerForm(setSmsForm);

        const submittedSmsCode = connectSubmit(async ({codeFromSms}: {codeFromSms: string}) => {
            const response = await submitTravelRuleCommit(http, {
                codeFromSms,
                holder,
                wallet: trt.fromAccount,
            }).catch(crash => {
                gotSmsSubmitOutcome({crash});
                return handleError(crash);
            });
            switch (response.status) {
                case 'Ok':
                    gotSmsSubmitOutcome({value: response.datum});
                    break;
                case 'BadRequest':
                    gotSmsSubmitOutcome({problem: response.datum});
                    break;
                case 'Abort':
                    break;
                default:
                    absurd(response);
            }
        });

        function gotSmsSubmitOutcome({value: trt, problem: issue}: Outcome<{id: TransactionId}, string>) {
            if (trt) {
                setFinish({txId: trt.id, amount});
                removeAlert();
            }
            if (issue === PinErrorCode.ExpiredCode) {
                setSmsForm(form => {
                    form.serverIssues = {codeFromSms: 'This code has expired'};
                });
            }
            if (issue === PinErrorCode.InvalidCode) {
                const onClose = () => {
                    setSmsForm(form => void (form.values.codeFromSms = ''));
                    TwoFaCodeForm.focusOTPInput();
                };
                dispatch(InvalidCodeDialog.open({onClose}));
            }
            if (issue === PinErrorCode.SusActivity) {
                dispatch(SusActivityDialog.open());
            }
        }

        const isSubmittingSmsForm = smsForm.submitInitAt > smsForm.submitDoneAt;
        return (
            <RootLayout common title='Confirmation'>
                <TwoFaCodeForm.ForCryptoTx
                    userPhone={queryOfUser.data.username}
                    provider={provider}
                    timer$={timer$}
                    onRetryIntent={() => submitForm({repeating: true, provider})}
                    otp={smsForm.values.codeFromSms}
                    onOtpChange={value => setSmsForm(userInputRecipe({codeFromSms: value}))}
                    otpLength={6}
                    onInputComplete={otp => submittedSmsCode({codeFromSms: otp})}
                    isPending={isSubmittingSmsForm}
                    issue={smsForm.serverIssues.codeFromSms}
                    onMethodChangeIntent={() => navigate(-1)}
                    deliveryInfo={direction.delivery ?? null}
                    onDeliveryInfoDismiss={() => navigate(-1)}
                />
            </RootLayout>
        );
    } else if (direction.step == Step.fee) {
        return (
            <ContinuationFeeStep
                trt={trt}
                amountsQuery={amountsQuery}
                limits={assetR.data.amountLimit}
                onNextStep={() => setDirection({step: Step.twoFaPick}, {replace: true})}
                onNotEnoughFunds={() => setDirection({step: Step.notEnoughFunds})}
            />
        );
    } else {
        return <NavigateToEntry entry={entry} />;
    }
}

function ContinuationFeeStep({
    trt,
    onNextStep,
    onNotEnoughFunds,
    limits,
    amountsQuery,
}: {
    trt: TrTx;
    amountsQuery: QueryObserverResult<Outcome<CalculatedAmounts, {amount?: string}>>;
    onNextStep: () => void;
    onNotEnoughFunds: () => void;
    limits: SendTxResources['amountLimit'];
}) {
    const {data: {value: amounts, problem: issues} = {}, isPending} = amountsQuery;
    const http = useHttpClient();
    const logout = useLogout();
    const handleError = useErrorHandler();

    const navigate = useNavigate();
    const useInHistoryState = useInHistoryStateNamespace(useId(), navigate);
    const [dialog, goToDialog] = useInHistoryState(Dialogs);

    const atoms = useStore();

    const rate = useRateToPrimaryCurrency(trt.currency);
    const {format: formatAsPrimary} = usePrimaryCurrencyFormat({approx: true});
    const {format: formatAsSubject} = useCurrencyFormat(trt.currency);
    const {format: formatAsSubjectBlockchain} = useCurrencyFormat(trt.fromAccount.blockchain);
    const renderDetail = (
        formatter: CurrencyFormatter.Fn,
        value: BigNumber | undefined,
        opt?: CurrencyFormatter.FnOptions
    ) => {
        if (!amountsQuery.isSuccess) {
            return <OperationDetail.Skeleton />;
        }
        if (!value) {
            return formatter(0, opt);
        }
        return formatter(value, opt);
    };

    // const isOnOwnBlockchain = trt.currency.code === trt.fromAccount.blockchain.currencyCode;

    const cantSubmit = isPending || !amounts;

    const act = {
        clickedViewDetails() {
            goToDialog({name: DialogName.trtDetails});
        },
        clickedFeeInfo() {
            goToDialog({name: DialogName.trtFeeInfo});
        },
        clickedCancelTransaction() {
            invariant(CANCELLATION_ENABLED, 'Cancellation is disabled');
            goToDialog({name: DialogName.cancelGuard});
        },
        clickedApproveOnCancel: async () => {
            invariant(CANCELLATION_ENABLED, 'Cancellation is disabled');
            const rollback = atoms.set(canceledTrTxIds$, trt.id);
            const response = await submitTrtCancellation(http, trt.id).catch(crash => {
                rollback();
                return handleError(crash);
            });
            switch (response.status) {
                case 200:
                    return;
                case 401:
                    logout();
                    break;
                default:
                    absurd(response);
            }
        },
        closeDialog() {
            if (dialog) {
                navigate(-1);
            }
        },
    };

    const t = {
        SendingHeading: `Sending ${trt.currency.code}`,
        YouSend: `You spend`,
        ApproxValue: 'Approx value',
        TransactionFee: 'Transaction fee',
        ViewOperationDetails: 'View operation details',
        BeneficiaryGets: 'Beneficiary gets',
        NextStep: 'Next step',
        CancelTransaction: 'Cancel the transaction',
    };

    const checkIsEnoughBalance = () => {
        if (!amounts?.amountToBeSent) {
            return true;
        }

        return !(amounts?.amountToBeReceived
            ? limits.upper.lt(amounts?.amountToBeReceived)
            : limits.upper.lt(amounts?.amountToBeSent));
    };

    const handleSubmit = async (ev: FormEvent<HTMLFormElement>) => {
        ev.preventDefault();
        if (!checkIsEnoughBalance()) {
            onNotEnoughFunds();
            return;
        }
        onNextStep();
    };

    if (issues?.amount) {
        return <TrTxNotEnoughFundsPage trt={trt} amounts={amounts ?? null} />;
    }

    return (
        <RootLayout common title={'Confirmation'}>
            <form className='contents' onSubmit={handleSubmit}>
                <h2 className='fv-heading-l text-neutral-100'>{t.SendingHeading}</h2>

                <OperationDetail.Layout>
                    <OperationDetail label={t.TransactionFee}>
                        {renderDetail(formatAsSubjectBlockchain, amounts?.feeAmount)}
                    </OperationDetail>
                    <OperationDetail label={t.YouSend}>
                        {renderDetail(formatAsSubject, amounts?.amountToBeSent)}
                    </OperationDetail>
                    <OperationDetail label={t.ApproxValue}>
                        {renderDetail(formatAsPrimary, amounts?.amountToBeSent.multipliedBy(rate))}
                    </OperationDetail>
                    <OperationDetail.Total label={t.BeneficiaryGets}>
                        {renderDetail(formatAsSubject, amounts?.amountToBeReceived)}
                    </OperationDetail.Total>
                </OperationDetail.Layout>

                <RsnTextButton.Inline onClick={act.clickedViewDetails} decoration='arrow-right'>
                    {t.ViewOperationDetails}
                </RsnTextButton.Inline>

                <div className='mt-auto'></div>

                <div className='grid gap-2'>
                    <RsnButton.Primary type='submit' disabled={cantSubmit}>
                        {t.NextStep}
                    </RsnButton.Primary>
                    {CANCELLATION_ENABLED && (
                        <RsnButton.Cautionary onClick={act.clickedCancelTransaction}>
                            {t.CancelTransaction}
                        </RsnButton.Cautionary>
                    )}
                </div>
            </form>

            <RsnDialog.Controller onClose={act.closeDialog}>
                {(() => {
                    switch (dialog?.name) {
                        case DialogName.cancelGuard:
                            return <AreYouSureCancelDialog trtId={trt.id} onApprove={act.clickedApproveOnCancel} />;
                        case DialogName.trtDetails:
                            return <TrtDetailsDialog trt={trt} amounts={amounts ?? null} />;
                        case DialogName.trtFeeInfo:
                            return <TxFeeInfoDialog />;
                    }
                    return undefined;
                })()}
            </RsnDialog.Controller>
        </RootLayout>
    );
}

export function ContinuationFeeStepLoading() {
    return (
        <RootLayout common title={'Confirmation'}>
            <div className='contents'>
                <RsnSkeleton height={28} width={150} />

                <div className={'flex flex-col gap-2'}>
                    <RsnSkeleton height={16} width={100} />
                    <RsnSkeleton height={44} width={'100%'} borderRadius={8} />
                </div>

                <div className='mt-auto'></div>

                <div className='grid gap-2'>
                    <RsnSkeleton height={52} width={'100%'} borderRadius={16} />
                    {CANCELLATION_ENABLED && <RsnSkeleton height={52} width={'100%'} borderRadius={16} />}
                </div>
            </div>
        </RootLayout>
    );
}

type DialogName = (typeof DialogName)[keyof typeof DialogName];
const DialogName = {
    cancelGuard: 'cancellationGuard',
    trtFeeInfo: 'trtFeeInfo',
    trtDetails: 'trtDetails',
} as const;

const Dialogs = type({
    name: type.enumerated(DialogName.cancelGuard, DialogName.trtFeeInfo),
}).or({
    name: type.enumerated(DialogName.trtDetails),
});
